import React, { useMemo, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { AuditLogEntry, AuditListParams } from '../../types/audit';
import { AuditEventFilters } from './auditEvents/AuditEventFilters';
import { AuditEventRow } from './auditEvents/AuditEventRow';
import { AuditPager } from './auditEvents/AuditPager';

interface AuditLogTableProps {
  items: AuditLogEntry[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  filters: AuditListParams;
  onFilterChange: (filters: AuditListParams) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onExportCsv?: () => void;
}

/**
 * The event list: the filter toolbar, one row per event (opened for its
 * diff), the pager. The page and the filters are owned by the caller,
 * which keeps them in the URL.
 */
const AuditLogTable: React.FC<AuditLogTableProps> = ({ items, loading, total, page, pageSize, filters, onFilterChange, onPageChange, onPageSizeChange, onExportCsv }) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState<number | null>(null);

  const counts = useMemo(() => {
    let success = 0;
    let failed = 0;
    let denied = 0;
    for (const it of items) {
      if (it.status === 'success') success++;
      else if (it.status === 'failed') failed++;
      else if (it.status === 'denied') denied++;
    }
    return { total, success, failed, denied };
  }, [items, total]);

  const filterOn = (key: 'actor_username' | 'action' | 'resource_type') => (value: string) => onFilterChange({ ...filters, [key]: value });
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = Object.keys(filters).some((k) => k !== 'authenticated_only');

  return (
    <div className="tpl-container audit-log-container">
      <div className="tpl-card">
        <AuditEventFilters filters={filters} onChange={onFilterChange} counts={counts} loading={loading} onExportCsv={onExportCsv} />

        {loading ? (
          <div className="tpl-state"><div className="tpl-spinner" /><p>{t('audit.loading')}</p></div>
        ) : items.length === 0 ? (
          <div className="tpl-empty" data-testid="audit-empty">
            <h3>{t('audit.empty')}</h3>
            {hasFilters && (
              <p>
                <button type="button" className="tpl-link" onClick={() => onFilterChange({})} data-testid="audit-empty-clear">
                  {t('audit.emptyClearFilters')}
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="tpl-table-wrap">
            <table className="tpl-table aud-table" data-testid="audit-table">
              <colgroup><col className="c-when" /><col className="c-actor" /><col className="c-action" /><col className="c-resource" /><col className="c-status" /><col className="c-method" /><col /><col className="c-open" /></colgroup>
              <thead>
                <tr>
                  <th>{t('audit.table.when')}</th>
                  <th>{t('audit.table.actor')}</th>
                  <th>{t('audit.table.action')}</th>
                  <th>{t('audit.table.resource')}</th>
                  <th>{t('audit.table.status')}</th>
                  <th>{t('audit.table.method')}</th>
                  <th>{t('audit.table.path')}</th>
                  <th className="tpl-col-open"><span className="sr-only">{t('audit.table.details')}</span></th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <AuditEventRow
                    key={entry.id}
                    entry={entry}
                    expanded={expanded === entry.id}
                    onToggle={() => setExpanded(expanded === entry.id ? null : entry.id)}
                    onFilterActor={filterOn('actor_username')}
                    onFilterAction={filterOn('action')}
                    onFilterResourceType={filterOn('resource_type')}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AuditPager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} />
      </div>
    </div>
  );
};

export default AuditLogTable;
